/**
 * The page the bank returns the donor to.
 *
 * ## Why this page exists at all
 *
 * The API's 3-D Secure return route used to answer with a small HTML page of
 * its own, carrying an inline `postMessage` script, so the checkout could hear
 * that the challenge was over. The deployed Content-Security-Policy forbids
 * inline script, so the browser blocked that script: the donor was left with a
 * blank frame and a checkout that never finished.
 *
 * The API now answers with a **redirect** to this page instead. This page is
 * served by the web app, under the web app's own policy, and it does the one
 * thing the old inline script did — it tells the parent frame the challenge is
 * over — with no inline script of its own.
 *
 * ## The status in the URL is a hint, never a verdict
 *
 * `?status=complete` says the *server* validated the authentication. It does
 * not say the donation succeeded: that is decided by the capture, and the
 * server decides it. So this page never tells the donor that the payment
 * worked, and a missing or unrecognised status is treated as "carry on" rather
 * than "failed" — guessing "failed" would throw away a payment that actually
 * succeeded and show the donor a failure for a gift that was taken.
 *
 * The checkout that receives the message continues to pay and asks the server
 * what really happened.
 *
 * ## When it is not inside a frame
 *
 * The normal path is: the challenge frame navigates here, this page posts to
 * its parent, the parent carries on. But the frame can also be opened on its
 * own — a donor reloads the frame, or copies its address into a new tab. There
 * is no parent to talk to then, so the page says what happened and offers a
 * way back, instead of posting into a void and showing nothing at all.
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/** What the server said happened. A hint, never a verdict. */
export type ThreeDsOutcome = 'complete' | 'failed';

/** The message this page posts to the parent frame. */
export interface ThreeDsResultMessage {
  type: '3ds-complete' | '3ds-failed';
  /** The donation, when the server knew it. */
  donationId?: string;
}

/**
 * The outcome the server reported, or `null` when it reported nothing we
 * recognise.
 */
export function outcomeFromSearch(search: string): ThreeDsOutcome | null {
  const status = new URLSearchParams(search).get('status');
  if (status === 'complete') return 'complete';
  if (status === 'failed') return 'failed';
  return null;
}

/** The donation the server named, when it named one. */
export function donationIdFromSearch(search: string): string | undefined {
  const value = new URLSearchParams(search).get('donationId');
  return value && value.length > 0 ? value : undefined;
}

/**
 * The message to post, built from the query string.
 *
 * Exported so the rule can be tested without a browser — this is the one place
 * where a hint becomes something the checkout acts on, and "what happens when
 * the hint is missing" is the question worth asking about it.
 */
export function resultMessageFromSearch(search: string): ThreeDsResultMessage {
  const outcome = outcomeFromSearch(search);
  const donationId = donationIdFromSearch(search);
  // A missing or unknown status means "carry on", never "failed".
  //
  // `3ds-complete` makes the checkout ask the server whether the payment
  // worked, and the server is the only thing that knows. `3ds-failed` stops the
  // checkout and shows the donor a failure screen — so guessing it would
  // discard a payment that actually succeeded.
  const type = outcome === 'failed' ? '3ds-failed' : '3ds-complete';
  return donationId ? { type, donationId } : { type };
}

export default function ThreeDSComplete() {
  const { t } = useTranslation();

  // Whether this page is inside a frame. Read on every render rather than
  // stored in state: it cannot change while the page is alive, and computing it
  // once would leave the first render — the one a donor might see — showing
  // the wrong thing.
  const framed = typeof window !== 'undefined' && window.parent !== window;

  useEffect(() => {
    // Nothing to tell when there is no parent to tell.
    if (!framed) return;
    const message = resultMessageFromSearch(window.location.search);
    // The target origin is this page's own origin: the parent is the checkout
    // page of the same web app, and naming an exact origin means the message
    // is delivered to that one window and to no other.
    window.parent.postMessage(message, window.location.origin);
  }, [framed]);

  return (
    <main className="three-ds-complete" data-testid="three-ds-complete">
      <h1>{t('threeDsComplete.title')}</h1>
      {framed ? (
        <p data-testid="three-ds-complete-working">{t('threeDsComplete.working')}</p>
      ) : (
        <>
          <p data-testid="three-ds-complete-unframed">{t('threeDsComplete.unframedBody')}</p>
          <a className="three-ds-complete__back" href="/give" data-testid="three-ds-complete-back">
            {t('threeDsComplete.backToCheckout')}
          </a>
        </>
      )}
    </main>
  );
}
